import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { ProblemListMobile } from '@/public/features/problem/ui/ProblemListMobile'
import { ProblemListPc } from '@/public/features/problem/ui/ProblemListPc'
import { api } from '@/shared/lib/api'
import { PUBLIC_STALE_TIME_MS } from '@/shared/lib/query'
import { URGENCY_LABEL, makeLabeler, useCommonCodes, useDongs, useTargetCodes } from '@/shared/lib/codes'

const PAGE_SIZE = 24

export function ProblemListPage() {
  const [category, setCategory] = useState('')
  const [target, setTarget] = useState('')
  const [region, setRegion] = useState('')
  const [urgency, setUrgency] = useState('')

  const problems = useInfiniteQuery({
    queryKey: ['problem-cards', { category, target, region, urgency }],
    queryFn: ({ pageParam }) =>
      api.problemCards({ category, target, region, urgency, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, page) => n + page.items.length, 0)
      return loaded < last.total ? loaded : undefined
    },
    staleTime: PUBLIC_STALE_TIME_MS,
  })

  const loaded = problems.data?.pages.flatMap((page) => page.items) ?? []

  const categories = useCommonCodes('category')
  const targets = useTargetCodes()
  const dongs = useDongs()

  const categoryLabel = makeLabeler(categories.data)
  const targetLabel = makeLabeler(targets.data)

  return (
    <>
      <ProblemListMobile query={problems} />
      <ProblemListPc query={problems} />

      <div className="desktop-screen">
      <div style={{ display: 'flex', alignItems: 'start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 className="m-0 mb-1.5 text-headline-sm font-bold tracking-[-0.02em]">시민문제카드</h1>
          <p className="m-0 mb-7 text-body-sm text-black-100">시민이 등록한 동네의 생활 문제입니다.</p>
        </div>
        <Link className="cursor-pointer rounded-lg border border-black-600 bg-black-600 px-[22px] py-[11px] font-semibold text-white-50 disabled:cursor-not-allowed disabled:opacity-50" to="/problems/new">
          + 문제 등록
        </Link>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="분야">
          <option value="">분야 전체</option>
          {categories.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="대상">
          <option value="">대상 전체</option>
          {targets.data?.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select value={region} onChange={(e) => setRegion(e.target.value)} aria-label="동네">
          <option value="">동네 전체</option>
          {dongs.data?.map((r) => (
            <option key={r.code} value={r.code}>
              {r.name}
            </option>
          ))}
        </select>
        <select value={urgency} onChange={(e) => setUrgency(e.target.value)} aria-label="시급성">
          <option value="">시급성 전체</option>
          <option value="high">시급</option>
          <option value="mid">보통</option>
          <option value="low">여유</option>
        </select>
      </div>

      <QueryState
        isPending={problems.isPending}
        error={problems.error}
        isEmpty={loaded.length === 0}
        emptyMessage="조건에 맞는 시민문제가 없습니다."
      >
        <p className="text-black-100 text-label" style={{ marginBottom: 12 }}>
          {problems.data?.pages[0]?.total}개 문제
        </p>
        <div className="grid grid-cols-1 gap-3.5 min-[601px]:grid-cols-2 min-[901px]:grid-cols-3">
          {loaded.map((p) => (
            <Link key={p.id} className="block rounded-[10px] border border-white-300 bg-white-50 p-[18px] transition-[border-color] duration-150 ease-linear hover:border-white-400" to={`/problems/${p.id}`}>
              <div className="mb-2 text-caption text-white-900">
                {p.region.name}{' '}
                <span className={[
                    'inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold',
                    p.urgency === 'high'
                      ? 'bg-black-600 text-white-50'
                      : p.urgency === 'mid'
                        ? 'bg-white-200 text-black-500'
                        : 'border border-white-400 bg-transparent text-white-900',
                  ].join(' ')}>
                  {URGENCY_LABEL[p.urgency] ?? p.urgency}
                </span>
              </div>
              <h3>{p.title}</h3>
              <div className="flex flex-wrap gap-[5px]">
                {(p.categoryCodes ?? []).map((code) => (
                  <span key={code} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                    {categoryLabel(code)}
                  </span>
                ))}
                {p.targetCodes.map((code) => (
                  <span key={code} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                    {targetLabel(code)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </QueryState>
      </div>
    </>
  )
}
